import { useMemo } from 'react';
import {
    AnyFinancialEntity,
    ExternalFinancialEntity,
    feBaseConfigs,
    FinancialCategory,
    getCurrentValue,
    GoalAccountWithGoal,
    PlentyGoalAccountFinancialEntity,
} from '../../financialEntities';
import { Goal } from '../../goals';
import { sampleFinancialEntities, sampleGoals } from '../../sampleData';
import { FEsData, useFinancialEntitiesContext } from '../financialEntities';
import {
    getGoalAccountBalance,
    getGoalBalance,
    getGoalBaseConfig,
    getIsPartnerGoalOnboardingInProgress,
    getJointGoalAccount,
    getMyGoalAccount,
    isGoalSetupIncomplete,
} from '../goals';
import { useCurrentUser } from '../user';

export type Account = (Goal | GoalAccountWithGoal | ExternalFinancialEntity) & {
    category: FinancialCategory;
    value: number;
};

export const useAccounts = ({
    returnSampleDataIfEmpty,
}: {
    returnSampleDataIfEmpty: boolean;
}) => {
    const { fes } = useFinancialEntitiesContext();
    const { userInfo } = useCurrentUser();

    // TODO: Show incomplete goals to all users once goal onboarding is finished
    const includeIncompleteGoals = userInfo.isAdmin;

    return useMemo(() => {
        return {
            accounts: getAccounts({
                data: fes.data,
                unfilteredData: fes.unfilteredData,
                returnSampleDataIfEmpty,
                includeIncompleteGoals,
            }),
            loading: fes.loading,
        };
    }, [
        fes.data,
        fes.loading,
        fes.unfilteredData,
        returnSampleDataIfEmpty,
        includeIncompleteGoals,
    ]);
};

const getAccounts = ({
    data,
    unfilteredData,
    returnSampleDataIfEmpty,
    includeIncompleteGoals,
}: {
    data?: FEsData;
    unfilteredData?: FEsData;
    returnSampleDataIfEmpty: boolean;
    includeIncompleteGoals: boolean;
}): Account[] => {
    if (!data || !unfilteredData) return [];

    const financialEntities =
        unfilteredData.financialEntities.length === 0 && returnSampleDataIfEmpty
            ? (sampleFinancialEntities as AnyFinancialEntity[])
            : data.financialEntities;

    let goals =
        unfilteredData.financialEntities.length === 0 && returnSampleDataIfEmpty
            ? (sampleGoals as Goal[])
            : data.goals;
    if (!includeIncompleteGoals) {
        const incompleteGoalIds = unfilteredData.goals
            .filter((goal) => !isGoalSetupIncomplete(goal))
            .map((goal) => goal.id);
        goals = goals.filter((goal) => incompleteGoalIds.includes(goal.id));
    }

    const accounts: Account[] = [];

    financialEntities.forEach((fe) => {
        if (fe.type === 'PlentyGoalAccount') return;
        accounts.push({
            ...fe,
            category: feBaseConfigs[fe.type].category,
            value: getCurrentValue(fe) ?? 0,
        });
    });

    goals.forEach((goal) => {
        // First, retirement is Retirement.
        if (goal.type === 'RETIREMENT') {
            // Retirement is a special case, since we want to render each Plenty
            // IRA account as an item but *not* the retirement goal as an item.
            const iraAccounts = financialEntities.filter(
                (fe) =>
                    fe.type === 'PlentyGoalAccount' &&
                    ['TRADITIONAL_IRA', 'ROTH_IRA'].includes(
                        fe.info.accountType
                    )
            ) as PlentyGoalAccountFinancialEntity[];
            iraAccounts.forEach((fe) =>
                accounts.push({
                    ...fe,
                    goal,
                    category: 'Retirement',
                    value: getGoalAccountBalance(fe) ?? 0,
                })
            );
            return;
        }

        // If goal was created by partner and partner has not completed goal
        // onboarding, avoid listing any accounts for the goal.
        if (getIsPartnerGoalOnboardingInProgress(goal)) return;

        // Next, check if goal has account; if so, use goal account investment type for group.
        const ga =
            goal.accountStructure === 'JOINT'
                ? getJointGoalAccount(goal)
                : getMyGoalAccount(goal);
        if (ga) {
            const category =
                ga.info.investmentType === 'HIGH_YIELD_CASH'
                    ? 'CashAccount'
                    : 'Investment';
            accounts.push({
                ...goal,
                category,
                value: getGoalBalance(goal) ?? 0,
            });
            return;
        }
        // Finally, if no goal account yet, use goal config to determine group.
        const goalGroup = getGoalBaseConfig(goal.type).group;
        switch (goalGroup) {
            case 'HIGH_YIELD_CASH_ONLY':
                accounts.push({
                    ...goal,
                    category: 'CashAccount',
                    value: getGoalBalance(goal) ?? 0,
                });
                return;
            case 'INVEST':
                accounts.push({
                    ...goal,
                    category: 'Investment',
                    value: getGoalBalance(goal) ?? 0,
                });
                return;
            case 'TRACK':
                // TODO: Handle this when we enable TRACK goals
                return;
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _exhaustiveCheck: never = goalGroup;
            }
        }
    });

    return accounts;
};
